import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { Col, Row, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Field, change, formValueSelector } from "redux-form";

import { RequiredMark, Skeleton } from "components";
import FeesTemplateContext from "components/applicationFormFields/fees/feesTemplateContext";
import { CustomDropdownById } from "components/formFields/dropdown";
import DropdownReactSelect from "components/formFields/dropdownReactSelect";
import {
	PROCESSOR_SPONSOR_SECTION_NAME,
	RESIDUAL_SECTION_NAME,
} from "globalConstants";
import {
	dispatchChangeScheduleAFirstAsDefault,
	getDefaultSponsorPerProcessor,
} from "helpers";
import { useLoader } from "hooks";
import { filterScheduleAsAccordingProcessor } from "mappers";
import { useProcessorsQuery, useSponsorsQuery } from "queries";
import { dropdownValuesActions } from "store";
import { Processors, Sponsors } from "types";
import { required } from "validators/validation";

import { ReactComponent as WarningIcon } from "static/images/warning-alert.svg";

const ProcessorSponsorSection = ({
	allLoading,
	dealStage,
	formName,
	sectionName = PROCESSOR_SPONSOR_SECTION_NAME,
	residualsSectionName = RESIDUAL_SECTION_NAME,
	initialTabIndex,
	setPDFTemplateType,
	isResetResidualsValuesOnProcessorChange = false,
	hideSponsor,
	applicationStatus = {},
	applicationId,
	formValues,
	rawScheduleAs,
	setFilteredScheduleAs,
}) => {
	const dispatch = useDispatch();

	const { name: appStatusName } = applicationStatus;
	const isUnderwriting = useMemo(
		() => appStatusName === "Underwriting",
		[appStatusName],
	);
	const isMerchant = useMemo(
		() => appStatusName === "Merchant",
		[appStatusName],
	);

	const selector = formValueSelector(formName);

	const getProcessorId = state =>
		selector(state, `${sectionName}.processor.id`);

	const getSponsorId = state => selector(state, `${sectionName}.sponsor.id`);

	const processorId = parseInt(useSelector(getProcessorId));
	const sponsorId = parseInt(useSelector(getSponsorId));

	const { data: processors, isLoading: isLoadingProcessors } =
		useProcessorsQuery({
			applicationId,
			pageName: dealStage,
		});

	const { data: sponsors, isLoading: isLoadingSponsors } = useSponsorsQuery({
		applicationId,
		pageName: dealStage,
	});

	const handleProcessorChange = e => {
		const processorId = e?.id;
		const primaryAgentId = formValues?.primaryAgentId;

		// on Merchant page no need to preselect ScheduleA
		if (!isMerchant && processorId && primaryAgentId) {
			const filteredScheduleAsAccordingProcessor =
				filterScheduleAsAccordingProcessor?.(
					rawScheduleAs,
					processorId,
				);
			setFilteredScheduleAs?.(filteredScheduleAsAccordingProcessor);
			dispatchChangeScheduleAFirstAsDefault(
				dispatch,
				formName,
				filteredScheduleAsAccordingProcessor,
			);
		} else {
			setFilteredScheduleAs?.([]);
			dispatch(
				change(formName, `${RESIDUAL_SECTION_NAME}.scheduleA.id`, ""),
			);
		}

		const sponsor = getDefaultSponsorPerProcessor(
			+processorId,
			processors,
			sponsors,
		);

		if (sponsor) {
			// Set full sponsor data into form filed to show on App Summary page
			dispatch(
				change(
					formName,
					`${PROCESSOR_SPONSOR_SECTION_NAME}.sponsor`,
					sponsor,
				),
			);
		} else {
			dispatch(
				change(formName, `${PROCESSOR_SPONSOR_SECTION_NAME}.sponsor`, {
					id: "",
					name: "",
					type: "",
				}),
			);
		}

		setPDFTemplateType?.(e);
	};

	const handleSponsorChange = useCallback(
		e => {
			// Set full sponsor data
			const sponsor = sponsors?.find(
				x => x.id?.toString() === e?.target?.value?.toString(),
			);

			if (sponsor) {
				dispatch(
					change(
						formName,
						`${PROCESSOR_SPONSOR_SECTION_NAME}.sponsor`,
						sponsor,
					),
				);
			}
		},
		[dispatch, formName, sponsors],
	);

	useEffect(() => {
		dispatch(dropdownValuesActions.getPdfTemplates());
	}, [dispatch]);

	const { isLoading: isLoadingFees } = useContext(FeesTemplateContext);

	const Loader = useLoader(
		isLoadingFees && isLoadingProcessors && isLoadingSponsors,
	);

	useEffect(() => {
		if (
			isResetResidualsValuesOnProcessorChange &&
			(processorId === Processors.Elavon.value ||
				(processorId === Processors.TSYS.value &&
					sponsorId === Sponsors.Merrick.value))
		) {
			dispatch(
				change(formName, `${residualsSectionName}.scheduleA.id`, ""),
			);
		}
	}, [
		dispatch,
		formName,
		isResetResidualsValuesOnProcessorChange,
		processorId,
		residualsSectionName,
		sponsorId,
	]);

	const getProcessorOption = useCallback(
		processor => ({
			id: processor?.id,
			name: processor && processor?.name,
		}),
		[],
	);

	const processorOptions = useMemo(
		() => processors?.map(processor => getProcessorOption(processor)),
		[getProcessorOption, processors],
	);

	const processorSkeleton = (
		<Row>
			<Col sm={6}>
				<Form.Label column sm={6}>
					Processor
					<RequiredMark />
				</Form.Label>
			</Col>
			<Col sm={6}>
				<Skeleton height="38px" />
			</Col>
		</Row>
	);

	return (
		<>
			<Row>
				<Col md={5}>
					<Loader
						skeleton={processorSkeleton}
						component={
							<Field
								component={DropdownReactSelect}
								label="Processor"
								validate={required}
								name="processor"
								inputId={`${sectionName}.processor`}
								loading={allLoading || isLoadingProcessors}
								onChange={handleProcessorChange}
								options={processorOptions}
								requiredMark
								getOptionLabel={option => option?.name}
								getOptionValue={option => option?.id}
								warningIcon={
									<WarningIcon
										className="text-warning"
										width="10"
										style={{
											margin: "0 5px 4px 0",
										}}
									/>
								}
							/>
						}
					/>
				</Col>
				{!hideSponsor && (
					<Col md={{ span: 5, offset: 2 }}>
						<Field
							tabIndex={initialTabIndex + 1 || undefined}
							component={CustomDropdownById}
							label="Sponsor"
							validate={[required]}
							name="sponsor.id"
							loading={allLoading || isLoadingSponsors}
							items={sponsors}
							readOnly={!(isUnderwriting || isMerchant)}
							requiredMark={true}
							onChange={handleSponsorChange}
						/>
					</Col>
				)}
			</Row>
		</>
	);
};

export default ProcessorSponsorSection;
